import React, { useEffect, useMemo, useState } from 'react';
import { styled } from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../data/store';
import { showModal } from '../../data/stores/modalStore';
import {
	fetchCompanyTemplates,
	fetchCustomTemplates,
	deleteCompanyTemplate,
} from '../../services/template-service';
import CustomSwitch from '../common/Switch';
import ToggleButton from '../common/buttons/ToggleButton';
import { showAlert } from '../../data/stores/alertStore';
import { IconButton, Chip, Typography, CircularProgress } from '@mui/material';
import { Tooltip } from '@mui/material';
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';
import BusySpinner from '../common/BusySpinner';
import { getIconFromGoogleMimeType } from '../../utils/company-utils';
import {
	addTemplateThunk,
	removeTemplateThunk,
} from '../../data/thunks/projectConfigurationThunk';
import Table from '../common/Table';
import Paper from '../common/layout/Paper';
import { GridColDef, GridSortModel } from '@mui/x-data-grid';
import TemplateGoogleFileNotFoundModal from '../templates/TemplateGoogleFileNotFoundModal';
import Dialog, {
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
} from '../common/Dialog';
import Button from '../common/buttons/Button';

interface TableRow {
	id: string;
	templateName: string;
	type: string;
	status: string;
	template: any;
	hasError: boolean;
}

export default function ProjectTypeTemplates() {
	const {
		configuration: { templateIds, id: configurationId },
		loading,
	} = useSelector((state: RootState) => state.projectConfigurationState);

	const dispatch = useDispatch<AppDispatch>();
	const [templates, setTemplates] = useState<any[]>([]);
	const [initialized, setInitialized] = useState(false);
	const [selectedPanel, setSelectedPanel] = useState('all');
	const [sortModel, setSortModel] = useState<GridSortModel>([]);
	const [page, setPage] = useState(0);
	const [pageSize, setPageSize] = useState(50);
	const [selectedRows, setSelectedRows] = useState<string[]>([]);
	const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);
	const [pendingAction, setPendingAction] = useState<
		'enable' | 'disable' | null
	>(null);
	const [isBulkActionLoading, setIsBulkActionLoading] = useState(false);

	useEffect(() => {
		const fetchTemplates = async () => {
			const response = await fetchCompanyTemplates({
				take: 1000,
				where: { default: true },
			});
			const initialTemplates = response.list.map((t) => ({
				...t,
				selected: templateIds ? templateIds.includes(t.id) : false,
			}));
			const customTemplates = await fetchCustomTemplates();
			const customTemplatesList = customTemplates.map((t) => ({
				...t,
				selected: templateIds ? templateIds.includes(t.id) : false,
			}));
			setTemplates([...initialTemplates, ...customTemplatesList]);
			setInitialized(true);
		};
		fetchTemplates();
	}, [templateIds]);

	const transformedRows = useMemo((): TableRow[] => {
		if (!templates || templates.length === 0) return [];

		let filteredTemplates = templates;

		// Filter based on selected panel
		if (selectedPanel === 'default') {
			filteredTemplates = templates.filter((t) => t.default);
		} else if (selectedPanel === 'custom') {
			filteredTemplates = templates.filter((t) => !t.default);
		}

		return filteredTemplates.map((template) => ({
			id: template.id,
			templateName: template.name,
			type: template.default ? 'Default' : 'Custom',
			status: template.selected ? 'Enabled' : 'Disabled',
			template: template,
			hasError: !template.default && !template.verifiedTemplateExists,
		}));
	}, [templates, selectedPanel]);

	const handleTemplateToggle = async (id: string, checked: boolean) => {
		try {
			if (checked) {
				await dispatch(addTemplateThunk(configurationId, id));
			} else {
				await dispatch(removeTemplateThunk(configurationId, id));
			}

			// Update local state
			setTemplates((prevTemplates) =>
				prevTemplates.map((t) =>
					t.id === id ? { ...t, selected: checked } : t
				)
			);
		} catch (error) {
			dispatch(
				showAlert({
					message: 'Failed to update template',
					severity: 'error',
				})
			);
		}
	};

	const openGoogleFileNotFoundModal = (template: any) => {
		dispatch(
			showModal({
				openId: 'templateGoogleFileNotFoundModal',
				props: {
					companyTemplate: template,
					onSave: handleDeleteTemplate,
				},
			})
		);
	};

	const handleDeleteTemplate = async (templateId: string) => {
		try {
			await deleteCompanyTemplate(templateId);

			// Update local templates state to remove the deleted template
			setTemplates((prevTemplates) =>
				prevTemplates.filter((t) => t.id !== templateId)
			);

			dispatch(
				showAlert({
					message: 'Template deleted successfully',
					severity: 'success',
				})
			);
		} catch (error) {
			dispatch(
				showAlert({
					message: 'Failed to delete template',
					severity: 'error',
				})
			);
		}
	};

	const columns: GridColDef[] = [
		{
			field: 'templateName',
			headerName: 'Template Name',
			flex: 2,
			minWidth: 300,
			renderCell: (params) => {
				const icon = getIconFromGoogleMimeType(
					params.row.template.mimeType
				);
				return (
					<div style={{ display: 'flex', alignItems: 'center' }}>
						<img
							style={{
								height: 20,
								marginRight: 10,
								objectFit: 'cover',
							}}
							src={icon.icon}
							alt={icon.alt}
						/>
						{params.value}
					</div>
				);
			},
		},
		{
			field: 'type',
			headerName: 'Type',
			width: 120,
			renderCell: (params) => (
				<Chip
					label={params.value}
					size="small"
					color={params.value === 'Default' ? 'primary' : 'secondary'}
				/>
			),
		},
		{
			field: 'status',
			headerName: 'Status',
			width: 200,
			renderCell: (params) => {
				if (params.row.hasError) {
					return (
						<div
							style={{
								display: 'flex',
								alignItems: 'center',
								gap: '10px',
							}}
							onClick={(e) => e.stopPropagation()}
							onMouseDown={(e) => e.stopPropagation()}
						>
							<Tooltip title="The associated Google file does not exist">
								<IconButton
									size="small"
									onClick={(e) => {
										e.stopPropagation();
										openGoogleFileNotFoundModal(
											params.row.template
										);
									}}
								>
									<ErrorOutlineOutlinedIcon color="error" />
								</IconButton>
							</Tooltip>
							<span style={{ color: 'red', fontSize: '14px' }}>
								Remove from Orbito
							</span>
						</div>
					);
				}
				return (
					<div
						onClick={(e) => e.stopPropagation()}
						onMouseDown={(e) => e.stopPropagation()}
					>
						<CustomSwitch
							label={
								params.row.template.selected
									? 'Enabled'
									: 'Disabled'
							}
							checked={params.row.template.selected}
							onChange={(e) => {
								e.stopPropagation();
								handleTemplateToggle(
									params.row.template.id,
									e.target.checked
								);
							}}
						/>
					</div>
				);
			},
		},
	];

	const toggleOptions = [
		{
			value: 'all',
			display: 'All',
		},
		{
			value: 'default',
			display: 'Default',
		},
		{
			value: 'custom',
			display: 'Custom',
		},
	];

	const handleToggleChange = (value: string) => {
		setSelectedPanel(value);
		// Clear selection when filter changes
		setSelectedRows([]);
	};

	// Calculate counts for enable/disable buttons
	const selectedTemplatesData = useMemo(() => {
		// Get filtered templates based on selected panel
		let filteredTemplates = templates;
		if (selectedPanel === 'default') {
			filteredTemplates = templates.filter((t) => t.default);
		} else if (selectedPanel === 'custom') {
			filteredTemplates = templates.filter((t) => !t.default);
		}

		// Only count selected templates that are visible in current filter
		const selectedTemplates = filteredTemplates.filter((t) =>
			selectedRows.includes(t.id)
		);
		const enabledCount = selectedTemplates.filter((t) => t.selected).length;
		const disabledCount = selectedTemplates.filter(
			(t) => !t.selected
		).length;
		return { enabledCount, disabledCount, selectedTemplates };
	}, [selectedRows, templates, selectedPanel]);

	const handleBulkEnable = () => {
		setPendingAction('enable');
		setConfirmationModalOpen(true);
	};

	const handleBulkDisable = () => {
		setPendingAction('disable');
		setConfirmationModalOpen(true);
	};

	const handleConfirmBulkAction = async () => {
		if (!pendingAction || selectedRows.length === 0) return;

		const selectedCount = selectedRows.length;
		const selectedRowsCopy = [...selectedRows];

		setIsBulkActionLoading(true);

		try {
			const promises = selectedRowsCopy.map((templateId) => {
				if (pendingAction === 'enable') {
					return dispatch(
						addTemplateThunk(configurationId, templateId)
					);
				} else {
					return dispatch(
						removeTemplateThunk(configurationId, templateId)
					);
				}
			});

			await Promise.all(promises);

			// Update local state
			setTemplates((prevTemplates) =>
				prevTemplates.map((t) =>
					selectedRowsCopy.includes(t.id)
						? { ...t, selected: pendingAction === 'enable' }
						: t
				)
			);

			// Clear selection
			setSelectedRows([]);
			setConfirmationModalOpen(false);
			setPendingAction(null);

			dispatch(
				showAlert({
					message: `Successfully ${
						pendingAction === 'enable' ? 'enabled' : 'disabled'
					} ${selectedCount} template${selectedCount > 1 ? 's' : ''}`,
					severity: 'success',
				})
			);
		} catch (error) {
			dispatch(
				showAlert({
					message: `Failed to ${
						pendingAction === 'enable' ? 'enable' : 'disable'
					} templates`,
					severity: 'error',
				})
			);
		} finally {
			setIsBulkActionLoading(false);
		}
	};

	if (!initialized) {
		return <BusySpinner />;
	}

	return (
		<>
			<TemplateGoogleFileNotFoundModal />
			<Dialog
				open={confirmationModalOpen}
				handleClose={() => {
					if (!isBulkActionLoading) {
						setConfirmationModalOpen(false);
						setPendingAction(null);
					}
				}}
			>
				<DialogTitle>
					Confirm {pendingAction === 'enable' ? 'Enable' : 'Disable'}{' '}
					Templates
				</DialogTitle>
				<DialogContent>
					<DialogContentText>
						Are you sure you want to{' '}
						{pendingAction === 'enable' ? 'enable' : 'disable'}{' '}
						{selectedRows.length} template
						{selectedRows.length > 1 ? 's' : ''}?
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button
						onClick={() => {
							setConfirmationModalOpen(false);
							setPendingAction(null);
						}}
						variant="text"
						disabled={isBulkActionLoading}
					>
						Cancel
					</Button>
					<Button
						onClick={handleConfirmBulkAction}
						color={
							pendingAction === 'disable' ? 'error' : 'primary'
						}
						disabled={isBulkActionLoading}
						startIcon={
							isBulkActionLoading ? (
								<CircularProgress size={16} color="inherit" />
							) : undefined
						}
					>
						Confirm
					</Button>
				</DialogActions>
			</Dialog>
			<Container>
				<Typography
					variant="body2"
					color="text.secondary"
					sx={{ mt: 2, mb: 3 }}
				>
					Enable or disable templates that will be built for this
					project type.
				</Typography>
				<FilterContainer>
					<ToggleButton
						value={selectedPanel}
						onChange={handleToggleChange}
						options={toggleOptions}
					/>
					{selectedRows.length > 0 && (
						<ActionButtonsContainer>
							{selectedTemplatesData.disabledCount > 0 && (
								<Button
									onClick={handleBulkEnable}
									color="primary"
									variant="contained"
									style={{ marginRight: 8 }}
								>
									ENABLE (
									{selectedTemplatesData.disabledCount})
								</Button>
							)}
							{selectedTemplatesData.enabledCount > 0 && (
								<Button
									onClick={handleBulkDisable}
									color="error"
									variant="contained"
								>
									DISABLE (
									{selectedTemplatesData.enabledCount})
								</Button>
							)}
						</ActionButtonsContainer>
					)}
				</FilterContainer>

				<TableContainer>
					<Table
						title={''}
						columns={columns}
						rows={transformedRows}
						rowCount={transformedRows.length}
						loading={loading}
						sortModel={sortModel}
						onSortModelChange={setSortModel}
						page={page}
						pageSize={pageSize}
						onPaginationModelChange={(model) => {
							setPage(model.page);
							setPageSize(model.pageSize);
						}}
						showSimpleRowCount={true}
						autoHeight={false}
						checkboxSelection={true}
						selectionModel={selectedRows}
						onSelectionModelChange={(newSelection) => {
							setSelectedRows(newSelection as string[]);
						}}
						boxSx={{
							height: 'calc(100vh - 305px)',
							'& .MuiDataGrid-row': {
								'&:hover': {
									backgroundColor: 'action.hover',
								},
							},
						}}
					/>
				</TableContainer>
			</Container>
		</>
	);
}

const Container = styled.div`
	display: flex;
	flex-direction: column;
	width: 100%;
`;

const FilterContainer = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	margin-bottom: 16px;
`;

const ActionButtonsContainer = styled.div`
	display: flex;
	gap: 8px;
	align-items: center;
`;

const TableContainer = styled(Paper)`
	flex: 1;
`;
